import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useCanGoBack, useHistory} from '@aboutyou/router';
import {
  BackButtonIconName,
  COLORS,
  FONT_SIZES,
  Icon,
  SPACING,
  UnstyledLinkLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useAppendFirstProductIdCallback} from '../../../features/firstProductId/useFirstProductIds';

export type BackToCategoryBarProps = {
  className?: string;
};

export const BackToCategoryBarView: FC<
  Partial<GetProductBulkResponse['linksSection']> & BackToCategoryBarProps
> = ({backLinkLabel, backLinkTarget, className}) => {
  const {t} = useTranslation();
  const [pathname, search] = (backLinkTarget?.url || '').split('?');
  const appendFirstProductIdCallback = useAppendFirstProductIdCallback();
  const queryParamsWithFirstProductId = appendFirstProductIdCallback(
    new URLSearchParams(search),
  );
  const backLinkWithFirstProductId = `${pathname}${queryParamsWithFirstProductId}`;

  const canGoBack = useCanGoBack();
  const {goBack} = useHistory();
  const showBackLink = canGoBack || backLinkWithFirstProductId;

  if (!showBackLink) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (canGoBack) {
      e.preventDefault();
      goBack();
    }
  };

  // RouterLink intercepts the click and performs an SPA push when no SPA
  // history exists. Using a raw <a href> here would trigger a full document
  // load and re-fire shop_init / gtm.js on the destination page.
  const to = !canGoBack ? backLinkWithFirstProductId : '';

  return (
    <Bar className={className} data-testid="backToCategoryBar">
      <BackLink
        data-testid={
          canGoBack
            ? 'backToCategoryBar-back-navigation'
            : 'backToCategoryBar-back-direct'
        }
        to={to}
        onClick={handleClick}
        aria-label={backLinkLabel ? undefined : t('Navigate back')}
      >
        <Icon size="XS" asset={BackButtonIconName.Back} aria-hidden="true" />
        {backLinkLabel && (
          <Label data-testid="backToCategoryBar-label">{backLinkLabel}</Label>
        )}
      </BackLink>
    </Bar>
  );
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  padding: ${SPACING['Y.Md']} ${SPACING['X.Lg']};
  background: ${COLORS.White};
  border-top: 1px solid ${COLORS['Grey.Shade100']};
`;

const BackLink = styled(UnstyledLinkLinaria)`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING['X.Sm']};
  padding: ${SPACING['X.Sm']};
  color: ${COLORS.Black};
  text-decoration: none;
  margin: 0;
  cursor: pointer;
  min-width: 0;
`;

const Label = styled.span`
  font-size: ${FONT_SIZES.px14};
  color: ${COLORS.Black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
